import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { Download, FileText, Loader2, Calendar, BarChart3 } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from "date-fns";

interface FinancialReportsTabProps {
    restaurantId: string;
    restaurantName: string;
}

export function FinancialReportsTab({ restaurantId, restaurantName }: FinancialReportsTabProps) {
    const { toast } = useToast();
    const [isDownloading, setIsDownloading] = useState(false);
    const [reportType, setReportType] = useState<'custom' | 'month' | 'year'>('month');
    const [selectedMonth, setSelectedMonth] = useState(() => format(new Date(), 'yyyy-MM'));
    const [selectedYear, setSelectedYear] = useState(() => format(new Date(), 'yyyy'));
    const [customStartDate, setCustomStartDate] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [customEndDate, setCustomEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));

    const getDateRange = () => {
        switch (reportType) {
            case 'month':
                // Use T00:00:00 for Safari compatibility
                const monthDate = new Date(selectedMonth + '-01T00:00:00');
                return {
                    startDate: format(startOfMonth(monthDate), 'yyyy-MM-dd'),
                    endDate: format(endOfMonth(monthDate), 'yyyy-MM-dd'),
                    label: format(monthDate, 'MMMM yyyy'),
                };
            case 'year':
                // Use T00:00:00 for Safari compatibility
                const yearDate = new Date(selectedYear + '-01-01T00:00:00');
                return {
                    startDate: format(startOfYear(yearDate), 'yyyy-MM-dd'),
                    endDate: format(endOfYear(yearDate), 'yyyy-MM-dd'),
                    label: selectedYear,
                };
            case 'custom':
                return {
                    startDate: customStartDate,
                    endDate: customEndDate,
                    label: `${customStartDate} to ${customEndDate}`,
                };
            default:
                return { startDate: '', endDate: '', label: '' };
        }
    };

    const handleDownloadReport = async () => {
        const { startDate, endDate, label } = getDateRange();

        if (!startDate || !endDate) {
            toast({
                title: "Invalid Date Range",
                description: "Please select a valid date range.",
                variant: "destructive",
            });
            return;
        }

        setIsDownloading(true);
        try {
            const response = await authenticatedFetch(
                `/api/reports/financial?startDate=${startDate}&endDate=${endDate}`
            );

            if (!response.ok) {
                throw new Error('Failed to generate report');
            }

            // Get the CSV content
            const csvContent = await response.text();

            // Create a blob and download link
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${restaurantName.replace(/[^a-z0-9]/gi, '_')}_Financial_Report_${label.replace(/[^a-z0-9]/gi, '_')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast({
                title: "Report Downloaded",
                description: `Financial report for ${label} has been downloaded.`,
            });
        } catch (error) {
            console.error('Error downloading report:', error);
            toast({
                title: "Download Failed",
                description: error instanceof Error ? error.message : "Failed to download report",
                variant: "destructive",
            });
        } finally {
            setIsDownloading(false);
        }
    };

    // Generate month options for the last 24 months
    const monthOptions = Array.from({ length: 24 }, (_, i) => {
        const date = subMonths(new Date(), i);
        return {
            value: format(date, 'yyyy-MM'),
            label: format(date, 'MMMM yyyy'),
        };
    });

    // Generate year options for the last 5 years
    const yearOptions = Array.from({ length: 5 }, (_, i) => {
        const date = subYears(new Date(), i);
        return {
            value: format(date, 'yyyy'),
            label: format(date, 'yyyy'),
        };
    });

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <BarChart3 className="h-8 w-8 text-primary" />
                <div>
                    <h2 className="text-2xl font-bold">Financial Reports</h2>
                    <p className="text-muted-foreground">Download financial summaries and order reports</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Report Generator Card */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Generate Financial Report
                        </CardTitle>
                        <CardDescription>
                            Export your order data and financial summary as a CSV file
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Report Type Selection */}
                        <div className="space-y-2">
                            <Label>Report Period</Label>
                            <Select value={reportType} onValueChange={(value: 'custom' | 'month' | 'year') => setReportType(value)}>
                                <SelectTrigger data-testid="select-report-type">
                                    <SelectValue placeholder="Select period type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="month">Monthly Report</SelectItem>
                                    <SelectItem value="year">Yearly Report</SelectItem>
                                    <SelectItem value="custom">Custom Date Range</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Month Selection */}
                        {reportType === 'month' && (
                            <div className="space-y-2">
                                <Label>Select Month</Label>
                                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                    <SelectTrigger data-testid="select-month">
                                        <SelectValue placeholder="Select month" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {monthOptions.map((month) => (
                                            <SelectItem key={month.value} value={month.value}>
                                                {month.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Year Selection */}
                        {reportType === 'year' && (
                            <div className="space-y-2">
                                <Label>Select Year</Label>
                                <Select value={selectedYear} onValueChange={setSelectedYear}>
                                    <SelectTrigger data-testid="select-year">
                                        <SelectValue placeholder="Select year" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {yearOptions.map((year) => (
                                            <SelectItem key={year.value} value={year.value}>
                                                {year.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Custom Date Range */}
                        {reportType === 'custom' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Start Date</Label>
                                    <Input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        data-testid="input-start-date"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>End Date</Label>
                                    <Input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        data-testid="input-end-date"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Download Button */}
                        <Button
                            onClick={handleDownloadReport}
                            disabled={isDownloading}
                            className="w-full mt-4"
                            data-testid="button-download-report"
                        >
                            {isDownloading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Generating Report...
                                </>
                            ) : (
                                <>
                                    <Download className="mr-2 h-4 w-4" />
                                    Download CSV Report
                                </>
                            )}
                        </Button>
                    </CardContent>
                </Card>

                {/* Report Information Card */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Report Contents
                        </CardTitle>
                        <CardDescription>
                            What's included in your financial report
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="p-4 bg-muted rounded-lg">
                                <h4 className="font-semibold mb-2">Summary Section</h4>
                                <ul className="text-sm text-muted-foreground space-y-1">
                                    <li>• Total number of orders</li>
                                    <li>• Total revenue (subtotal, tax, tips)</li>
                                    <li>• Average order value</li>
                                    <li>• Payment method breakdown</li>
                                    <li>• Order status summary</li>
                                </ul>
                            </div>

                            <div className="p-4 bg-muted rounded-lg">
                                <h4 className="font-semibold mb-2">Order Details</h4>
                                <ul className="text-sm text-muted-foreground space-y-1">
                                    <li>• Order ID and date/time</li>
                                    <li>• Items ordered with quantities</li>
                                    <li>• Subtotal, tax, tip, and total</li>
                                    <li>• Payment method and status</li>
                                    <li>• Table number (if applicable)</li>
                                </ul>
                            </div>

                            <div className="p-4 border border-primary/20 bg-primary/5 rounded-lg">
                                <p className="text-sm text-muted-foreground">
                                    <strong>Tip:</strong> Open the CSV file in Excel or Google Sheets
                                    for easy analysis, filtering, and creating charts.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
